// src/components/customer/NearbyShopAlert.tsx
// Közelségi értesítés komponens - mutatja, ha bolt van a közelben

import React from 'react';
import {
    Snackbar,
    Alert,
    Box,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontIcon from '@mui/icons-material/Storefront';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useNavigate } from 'react-router-dom';

interface NearbyShop {
    _id: string;
    name: string;
    address: string;
    distance: number;
}

interface NearbyShopAlertProps {
    shop: NearbyShop | null;
    open: boolean;
    onClose: () => void;
    onDismissForToday: () => void;
}

const NearbyShopAlert: React.FC<NearbyShopAlertProps> = ({
    shop,
    open,
    onClose,
    onDismissForToday,
}) => {
    const navigate = useNavigate();

    if (!shop) return null;

    const formatDistance = (meters: number): string => {
        if (meters < 1000) {
            return `${Math.round(meters)} m`;
        }
        return `${(meters / 1000).toFixed(1)} km`;
    };

    const handleAlertClick = () => {
        navigate(`/customer/shops/${shop._id}`);
        onClose();
    };

    const handleDismissToday = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDismissForToday();
    };

    return (
        <Snackbar
            open={open}
            autoHideDuration={3000} // 3 másodperc után eltűnik
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{ top: { xs: 70, sm: 80 }, maxWidth: '95vw', cursor: 'pointer' }}
            onClick={handleAlertClick}
        >
            <Alert
                severity="info"
                variant="filled"
                icon={<StorefrontIcon fontSize="small" />} // Kisebb ikon
                sx={(theme) => ({
                    width: '100%',
                    maxWidth: 350,
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(33, 150, 243, 0.95)'
                        : 'primary.main',
                    py: 0, // Kevesebb padding
                    alignItems: 'center',
                    '& .MuiAlert-icon': {
                        fontSize: 20,
                        py: 1,
                    },
                    '& .MuiAlert-message': {
                        py: 1,
                        width: '100%',
                        overflow: 'hidden',
                    },
                    '& .MuiAlert-action': {
                        p: 0,
                        pt: 0,
                    }
                })}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="Ma ne jelezz">
                            <IconButton
                                size="small"
                                aria-label="ma ne jelezz"
                                color="inherit"
                                onClick={handleDismissToday}
                                sx={{ p: 0.5, mr: 0.5, opacity: 0.8, '&:hover': { opacity: 1 } }}
                            >
                                <NotificationsOffIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            size="small"
                            aria-label="bezárás"
                            color="inherit"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            sx={{ p: 0.5 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                }
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        Bolt a közelben!
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.9 }}>
                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
                            {shop.name}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            • {formatDistance(shop.distance)}
                        </Typography>
                    </Box>
                </Box>
            </Alert>
        </Snackbar>
    );
};

export default NearbyShopAlert;
